import React, { useState, useContext } from "react";
import { ContactList } from "./ContactList";
import { ContactFilter } from "./ContactFilter";
import { Search, Plus, Grid3X3, List } from "lucide-react";
import { Pagination, Select } from "antd";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import DirectoryContext from "../../../context/directoryManagementContext";
import AddContactModal from "./AddContactModel";
import { useQuery, useQueryClient } from "react-query";

export const ContactsTab = (props) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const directoryContext = useContext(DirectoryContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalContacts,setTotalConstacts] = useState(0);
  const [totalUsers,setTotalUsers] = useState(0);
  const [totalPartyContacts,setTotalPartyContacts] = useState(0);
  const [totalPersonalContacts,setTotalPersonalContacts] = useState(0);
  const [totalFavouriteContacts,setTotalFavouriteContacts] = useState(0);

  // ---------- API fetcher ----------
  const fetchContacts = async () => {
  let url = "";

  if (searchTerm) {
    url = `api/directory/search?keyword=${searchTerm}&page=${currentPage}&limit=${limit}`;
    const res = await axiosInstance.get(url);
    return {
      results: res?.data?.data?.map(c => ({ ...c })),
      total: res?.data?.totalPages * limit,
    };
  }

  if (activeCategory === "users") {
    url = `api/directory/users`;
    const res = await axiosInstance.get(url);
    const all = res.data.data?.results?.map(c => ({ ...c, type: "users" })) || [];
    return {
      results: all.slice((currentPage - 1) * limit, currentPage * limit),
      total: all.length,
    };
  }

  if (activeCategory === "party") {
    url = `api/directory/party-members?page=${currentPage}&limit=${limit}`;
    const res = await axiosInstance.get(url);
    const all = res.data.data?.results.map(c => ({ ...c, type: "party" })) || [];
    return {
      results: all,
      total: res.data.data?.totalContacts,
    };
  }

  if (activeCategory === "personal") {
    url = `api/directory/other-contacts?page=${currentPage}&limit=${limit}`;
    const res = await axiosInstance.get(url);
    const all = res.data.data?.results.map(c => ({ ...c, type: "personal" })) || [];
    return {
      results: all,
      total: res.data.data?.totalContacts,
    };
  }

  if (activeCategory === "favourites") {
    url = `api/directory/favourites?page=${currentPage}&limit=${limit}`;
    const res = await axiosInstance.get(url);
    const all = res.data.data?.results || [];
    return {
      results: all,
      total: res.data.data?.totalFavouriteContacts || 0,
    };
  }

  if (activeCategory === "all") {
    url = `api/directory/all-contacts?page=${currentPage}&limit=${limit}`;
    const res = await axiosInstance.get(url);

    setTotalConstacts(res?.data?.data?.totalContacts || 0);
    setTotalUsers(res?.data?.data?.totalUsers || 0);
    setTotalPartyContacts(res?.data?.data?.totalPartyContacts || 0);
    setTotalPersonalContacts(res?.data?.data?.totalPersonalContacts || 0);
    setTotalFavouriteContacts(res?.data?.data?.totalFavouriteContacts || 0)
    
    return {
      results: res?.data?.data?.results || [],
      total: res?.data?.data?.totalContacts || 0,
    };
  }
};


  // ---------- React Query ----------
  const { data, isLoading, isError } = useQuery(
    ["contacts", activeCategory, searchTerm, currentPage, limit],
    fetchContacts,
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );
  const contacts = data?.results || [];
  const total = data?.total || 0;

  // ---------- Handlers ----------

  const toggleFavourite = async (id, type) => {
    try {
      await axiosInstance.patch(`/api/directory/favourite/${type}/${id}`);
      queryClient.invalidateQueries("contacts");
    } catch (error) {
      console.error("Error toggling favourite", error);
    }
  };

  const handleModal = () => {
    directoryContext.updateOpenModal(true);
  };

  return (
    <div className="directory-wrapper rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Contacts Directory</h2>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 !rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64 card-wrapper"
                />
              </div>

              <button
                onClick={handleModal}
                className="bg-[#004877] text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Contact</span>
              </button>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
         transition-all duration-300 
         rounded-3xl border border-white/30 
         backdrop-blur-3xl 
         bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-1 pl-2 pr-2 items-center space-x-2">
              <span className="text-sm text-gray-600">View:</span>
              <div className="flex rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === "grid"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === "list"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
              <ContactFilter
              activeCategory={activeCategory}
              onCategoryChange={(cat) => {
                setActiveCategory(cat);
                setCurrentPage(1);
              }}
              contactCounts={{
                all: totalContacts,
                users: totalUsers,
                party: totalPartyContacts,
                personal: totalPersonalContacts,
                favourites: totalFavouriteContacts
              }}
            />

            <div className="text-sm text-gray-500">
              {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {isLoading && <p>Loading...</p>}
        {isError && <p>Error fetching contacts</p>}
        {!isLoading && (
          <ContactList
            contacts={contacts}
            onToggleFavourite={toggleFavourite}
            viewMode={viewMode}
            setActiveTab={props.setActiveTab}
          />
        )}

        {/* Pagination */}
        <div className="mt-6 flex justify-between items-center">
          <div>
            <span>Show </span>
            <Select
              value={limit.toString()}
              style={{ width: 70 }}
              onChange={(val) => {
                setLimit(parseInt(val));
                setCurrentPage(1);
              }}
              options={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "50", label: "50" },
              ]}
            />
            <span> per page</span>
          </div>
          <Pagination
            current={currentPage}
            pageSize={limit}
            total={total}
            onChange={(page, pageSize) => {
              setCurrentPage(page);
              setLimit(pageSize);
            }}
          />
        </div>
      </div>

      <AddContactModal
          isOpen={directoryContext.isOpenModal}
          isAddMode={directoryContext.isAddMode}
      />
    </div>
  );
};
